/**
 * Timestamp helpers for the shared feedback thread.
 *
 * The RPC client can hand us a decoded Effect `DateTime.Utc` (which carries
 * `epochMillis`), an ISO string, a number, or a `Date` (optimistic bubbles stamp
 * an ISO string). `toEpochMs` normalises all of them WITHOUT `new Date(dateTimeUtc)`
 * — which produces "Invalid Date" on a DateTime.Utc object (see
 * agent_docs/standards/datetime-standards.md). Once we hold epoch millis, `new Date(ms)` is
 * valid and `toLocaleDateString` buckets by the viewer's local day.
 */
export function toEpochMs(value: unknown): number {
  if (value == null) return 0
  if (typeof value === "number") return value
  if (typeof value === "string") return new Date(value).getTime()
  if (value instanceof Date) return value.getTime()
  if (
    typeof value === "object" &&
    "epochMillis" in value &&
    typeof (value as { epochMillis: unknown }).epochMillis === "number"
  ) {
    return (value as { epochMillis: number }).epochMillis
  }
  return 0
}

/**
 * Compact relative time ("just now", "5m ago", "Yesterday", "3d ago", "2mo ago").
 * Output is intentionally identical to the legacy user-side and admin-side
 * formatters so the shared thread reproduces both surfaces byte-for-byte.
 */
export function formatRelativeTime(value: unknown): string {
  const diffMs = Date.now() - toEpochMs(value)
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 30) return `${diffDays}d ago`
  const diffMonths = Math.floor(diffDays / 30)
  return `${diffMonths}mo ago`
}

/**
 * Bubble timestamp: absolute local date + compact relative time, e.g.
 * "8/07/2026 · 5d ago". The date is built from local parts in the numeric
 * d/MM/yyyy layout (en-AU convention) so both the submitter and the admin read
 * the same shape regardless of ICU build.
 */
export function formatMessageTimestamp(value: unknown): string {
  const ms = toEpochMs(value)
  if (!ms) return ""
  const d = new Date(ms)
  const date = `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
  return `${date} · ${formatRelativeTime(value)}`
}

/** Local-day bucket key (`YYYY-M-D` in the runtime's timezone) for day grouping. */
export function localDayKey(epochMs: number): string {
  const d = new Date(epochMs)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/**
 * Day-separator label relative to `nowMs`: "Today" / "Yesterday" / "20 Jun".
 * Buckets by the viewer's LOCAL calendar day; "Yesterday" uses calendar
 * arithmetic (`new Date(y, m, d - 1)`) so it is correct across month/year and
 * DST boundaries.
 */
export function formatDayLabel(epochMs: number, nowMs: number): string {
  const dayKey = localDayKey(epochMs)
  const now = new Date(nowMs)
  const todayKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const yesterdayKey = `${y.getFullYear()}-${y.getMonth()}-${y.getDate()}`
  if (dayKey === todayKey) return "Today"
  if (dayKey === yesterdayKey) return "Yesterday"
  return new Date(epochMs).toLocaleDateString("en-AU", { day: "numeric", month: "short" })
}
